import { Icon } from "@iconify/react"
import type { IDisplaySection } from "@lexicanon/shared"
import { Badge, Card, Group, SimpleGrid, Stack, Text } from "@mantine/core"
import { useSpeakerColor } from "../../../contexts/SpeakerColors"
import { identityTextColor } from "../../../lib/speaker-colors"
import { useProduct } from "../../../lib/tenant"
import styles from "./GridSection.module.css"
import lexStyles from "./LexSection.module.css"
import { SectionHeader } from "./SectionHeader"

interface IProps {
  section: IDisplaySection
  data: unknown
}

// ── Game icon lookup ──────────────────────────────────────────────────────────

const SCENE_TYPE_ICONS: Record<string, string> = {
  exploration: "game-icons:treasure-map",
  combat: "game-icons:crossed-swords",
  roleplay: "game-icons:drama-masks",
  puzzle: "game-icons:puzzle",
  social: "game-icons:conversation",
  travel: "game-icons:walking-boot",
  rest: "game-icons:campfire",
}

const MOMENT_TYPE_ICONS: Record<string, string> = {
  dramatic: "game-icons:drama-masks",
  epic: "game-icons:laurel-crown",
  humor: "game-icons:jester-hat",
  combat: "game-icons:crossed-swords",
  mystery: "game-icons:magnifying-glass",
}

function getRoleIcon(role: string): string {
  const r = role.toLowerCase()
  if (r.includes("rogue") || r.includes("thief") || r.includes("assassin")) return "game-icons:hood"
  if (r.includes("cleric") || r.includes("priest") || r.includes("paladin")) return "game-icons:open-book"
  if (r.includes("wizard") || r.includes("mage") || r.includes("sorcerer") || r.includes("warlock"))
    return "game-icons:magic-swirl"
  if (r.includes("fighter") || r.includes("warrior") || r.includes("barbarian")) return "game-icons:sword-brandish"
  if (r.includes("ranger")) return "game-icons:bow-arrow"
  if (r.includes("druid")) return "game-icons:trefoil"
  if (r.includes("bard")) return "game-icons:lute"
  if (r.includes("monk")) return "game-icons:fist"
  return "game-icons:character"
}

function getVoxIcon(o: Record<string, unknown>): string {
  const type = typeof o.type === "string" ? o.type.toLowerCase() : ""
  const role = typeof o.role === "string" ? o.role : ""

  if (type && SCENE_TYPE_ICONS[type]) return SCENE_TYPE_ICONS[type]
  if (type && MOMENT_TYPE_ICONS[type]) return MOMENT_TYPE_ICONS[type]
  if (role) return getRoleIcon(role)
  if (o.name || o.title) return "game-icons:scroll-unfurled"
  return "game-icons:d10"
}

// ── Type guards & helpers ─────────────────────────────────────────────────────

function isRecord(val: unknown): val is Record<string, unknown> {
  return typeof val === "object" && val !== null && !Array.isArray(val)
}

/** Pick the first value from a record that is a non-empty string, or return "". */
function firstString(o: Record<string, unknown>, ...keys: string[]): string {
  for (const key of keys) {
    const v = o[key]
    if (typeof v === "string" && v.length > 0) return v
  }
  return ""
}

/** Coerce a record value to string (empty string if missing). */
function asStr(val: unknown): string {
  if (typeof val === "string") return val
  if (val == null) return ""
  return String(val)
}

// ── Component ─────────────────────────────────────────────────────────────────

export function GridSection({ section, data }: IProps): React.ReactElement | null {
  const product = useProduct()

  if (!Array.isArray(data) || data.length === 0) return null

  return (
    <div>
      <SectionHeader id={section.id} label={section.label} />
      <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="sm">
        {data.map((item, i) => (
          <GridCard
            key={`${section.id}-${isRecord(item) ? String(item.id ?? item.name ?? item.title ?? i) : String(item)}`}
            item={item}
            isVox={product === "voxcanon"}
          />
        ))}
      </SimpleGrid>
    </div>
  )
}

function GridCard({ item, isVox }: { item: unknown; isVox: boolean }): React.ReactElement {
  const resolvedTitleStr = isRecord(item) ? firstString(item, "title", "name", "speaker", "subject") : ""
  const speakerColorValue = useSpeakerColor(resolvedTitleStr)

  if (!isRecord(item)) {
    return (
      <Card withBorder p="sm">
        <Text size="sm">{String(item)}</Text>
      </Card>
    )
  }

  const o = item
  const title = firstString(o, "title", "name", "speaker", "subject")
  const subtitle = firstString(o, "role", "player", "type", "status")
  const body = asStr(o.description ?? o.highlights ?? o.progress)
  const quotes = o.notable_quotes ?? o.quotes
  const imageUrl = typeof o.imageUrl === "string" ? o.imageUrl : undefined
  const mood = typeof o.mood === "string" ? o.mood : undefined

  // ── Lexicanon: attendee card (has highlights = contribution data) ──────────
  if (!isVox && o.highlights !== undefined) {
    const color = speakerColorValue
    return (
      <Card withBorder p="sm">
        <Group justify="space-between" mb={4} wrap="nowrap">
          <Text fw={700} size="sm" c={identityTextColor(color)}>
            {title}
          </Text>
          {subtitle && (
            <Badge size="xs" variant="light" color={color} styles={{ label: { color: identityTextColor(color) } }}>
              {subtitle}
            </Badge>
          )}
        </Group>
        {body && (
          <Text size="sm" c="dimmed" lh={1.5}>
            {body}
          </Text>
        )}
      </Card>
    )
  }

  // ── Lexicanon: quote card (has speaker + quotes) ──────────────────────────
  if (!isVox && (o.quotes !== undefined || o.speaker !== undefined)) {
    const color = speakerColorValue
    return (
      <Card withBorder className={lexStyles.quoteCard}>
        <span className={lexStyles.quoteMark}>"</span>
        {Array.isArray(quotes) &&
          quotes.map((q, qi) => (
            <p key={qi} className={lexStyles.quoteText} style={{ margin: qi > 0 ? "6px 0 0" : 0 }}>
              {q}
            </p>
          ))}
        {title && (
          <div className={lexStyles.quoteSpeaker} style={{ color: identityTextColor(color) }}>
            — {title}
          </div>
        )}
      </Card>
    )
  }

  // ── Lexicanon: generic card ───────────────────────────────────────────────
  if (!isVox) {
    return (
      <Card withBorder p="sm">
        {title && (
          <Group justify="space-between" mb="xs">
            <Text fw={600} size="sm">
              {title}
            </Text>
            {subtitle && (
              <Badge size="xs" variant="light">
                {subtitle}
              </Badge>
            )}
          </Group>
        )}
        {body && (
          <Text size="sm" c="dimmed">
            {body}
          </Text>
        )}
        {Array.isArray(quotes) &&
          quotes.map((q, qi) => (
            <Text key={qi} size="xs" fs="italic" c="dimmed" mt={4}>
              "{q}"
            </Text>
          ))}
      </Card>
    )
  }

  // ── Voxcanon art mode: imageUrl present ──────────────────────────────────
  if (imageUrl) {
    return (
      <Card className={styles.voxCard} p={0}>
        <div className={styles.artImageWrap}>
          <img src={imageUrl} alt={title} />
          <div className={styles.artOverlay}>
            {mood && <span className={styles.voxMoodBadge}>{mood}</span>}
            <div className={styles.artMeta}>
              {title && (
                <Text className={styles.voxTitle} size="sm" style={{ flex: 1 }}>
                  {title}
                </Text>
              )}
              {subtitle && <span className={styles.voxSubtitle}>{subtitle}</span>}
            </div>
          </div>
        </div>
        {(body || (Array.isArray(quotes) && quotes.length > 0)) && (
          <Stack gap={4} p="sm">
            {body && <Text className={styles.voxBody}>{body}</Text>}
            {Array.isArray(quotes) &&
              quotes.map((q, qi) => (
                <span key={qi} className={styles.voxQuote}>
                  "{q}"
                </span>
              ))}
          </Stack>
        )}
      </Card>
    )
  }

  // ── Voxcanon icon mode: no imageUrl ──────────────────────────────────────
  const icon = getVoxIcon(o)
  return (
    <Card className={styles.voxCard} p="sm">
      <div className={styles.iconRow}>
        <div className={styles.iconAccent}>
          <Icon icon={icon} width={20} height={20} />
        </div>
        <div className={styles.iconMeta}>
          <Group justify="space-between" align="flex-start" wrap="nowrap" gap={6}>
            {title && (
              <Text className={styles.voxTitle} size="sm" style={{ flex: 1 }}>
                {title}
              </Text>
            )}
            {subtitle && <span className={styles.voxSubtitle}>{subtitle}</span>}
          </Group>
          {mood && <span className={styles.voxMoodBadge}>{mood}</span>}
        </div>
      </div>
      {(body || (Array.isArray(quotes) && quotes.length > 0)) && (
        <Stack gap={4} mt="xs" style={{ paddingLeft: 46 }}>
          {body && <Text className={styles.voxBody}>{body}</Text>}
          {Array.isArray(quotes) &&
            quotes.map((q, qi) => (
              <span key={qi} className={styles.voxQuote}>
                "{q}"
              </span>
            ))}
        </Stack>
      )}
    </Card>
  )
}
